import React, { useEffect, useState } from "react";
import "./text-input.scss";
import BlackEyeIcon from "../../SVG/black-eye";
import { BlackEyeStrokeIcon } from "../../SVG/black-eye-stroke";
import WarningIcon from "../../SVG/warning-icon";

interface TextInputProps {
  defaultValue?: string;
  required?: boolean;
  onBlur?: (data: { name: string; required: boolean; value: any }) => void;
  id: string;
  error?: string;
  readOnly?: boolean;
  label: string;
  hasError?: boolean;
  textHidden?: boolean;
  placeholder?: string;
  onChange?: (data: { name: string; required: boolean; value: any }) => void;
  resetField?: boolean;
  formatInput?: (value: string) => string; // Function to format the input
  labelSuffix?: React.ReactNode;
  hideLabel?: boolean;
  type?: "text" | "number"; // Add type prop
  onKeyDown?: (
    data: { name: string; required: boolean; value: any },
    event: React.KeyboardEvent<HTMLInputElement>
  ) => void;
  className?: string;
}

interface OnChangeData {
  name: string;
  required: boolean;
  value: any;
}

export function TextInput(props: TextInputProps) {
  const [value, setValue] = useState<string>(props.defaultValue || "");
  const [error, setError] = useState<string | undefined>(props.error);
  const [hasError, setHasError] = useState<boolean>(props.hasError || false);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  // Sync defaultValue
  useEffect(() => {
    if (props.defaultValue !== undefined) {
      const formattedValue = props.formatInput
        ? props.formatInput(props.defaultValue)
        : props.defaultValue;

      setValue(formattedValue);
    }
  }, [props.defaultValue]);

  // Sync error and hasError
  useEffect(() => {
    setError(props.error);
    setHasError(props.hasError ?? false);
  }, [props.error, props.hasError]);

  // Reset field value when resetField changes
  useEffect(() => {
    if (props.resetField) {
      resetField();
    }
  }, [props.resetField]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;

    // Apply input formatting if provided
    const formattedValue = props.formatInput
      ? props.formatInput(rawValue)
      : rawValue;

    setValue(formattedValue);

    const data: OnChangeData = {
      name: props.id,
      required: props.required ?? false,
      value: props.type === "number" ? Number(rawValue) : rawValue,
    };
    props.onChange?.(data);
  };

  const handleBlur = () => {
    const data: OnChangeData = {
      name: props.id,
      required: props.required ?? false,
      value: props.type === "number" ? Number(value) : value,
    };
    props.onBlur?.(data);
  };

  const resetField = () => {
    setValue("");
  };

  return (
    <div className={`custom-text-input-container ${props.className}`}>
      {!props?.hideLabel && (
        <label htmlFor={props.id}>
          {props.label}
          {props.required && <span className="required"> *</span>}
          {props.labelSuffix && (
            <span className="label-suffix">{props.labelSuffix}</span>
          )}
        </label>
      )}
      <div className="input-container">
        {props.textHidden ? (
          <>
            <input
              type={showPassword ? "text" : "password"}
              id={props.id}
              name={props.id}
              value={value}
              onChange={handleInputChange}
              required={props.required}
              onBlur={handleBlur}
              readOnly={props.readOnly}
              className={`custom-text-input ${
                error || hasError ? "error" : ""
              }`}
              placeholder={props.placeholder}
              onKeyDown={(event) =>
                props.onKeyDown?.(
                  {
                    name: props.id,
                    required: props.required ?? false,
                    value: props.type === "number" ? Number(value) : value,
                  },
                  event
                )
              }
            />
            <span
              onClick={() => setShowPassword(!showPassword)}
              className={showPassword ? "hide_pwd" : "show_pwd"}
            >
              {showPassword ? <BlackEyeStrokeIcon /> : <BlackEyeIcon />}
            </span>
          </>
        ) : (
          <input
            type={props.type || "text"}
            id={props.id}
            name={props.id}
            value={value}
            onChange={handleInputChange}
            required={props.required}
            onBlur={handleBlur}
            readOnly={props.readOnly}
            className={`custom-text-input ${error || hasError ? "error" : ""}`}
            placeholder={props.placeholder}
            onKeyDown={(event) =>
              props.onKeyDown?.(
                {
                  name: props.id,
                  required: props.required ?? false,
                  value: value,
                },
                event
              )
            }
          />
        )}
        {hasError && (
          <span className="input-warning-icon">
            <WarningIcon />
          </span>
        )}
      </div>
      {error && <div className="error">{error}</div>}
    </div>
  );
}
